import { useState } from "react";
import { Check, Search } from "lucide-react";
import { cn } from "@/lib/cn";

export type OpcaoDeChip = { id: string; rotulo: string; grupo?: string; apagada?: boolean };

/** Multisseleção em chips: as marcadas em cima, a lista (com busca quando é longa) embaixo. */
export function SeletorDeChips({
  opcoes,
  valor,
  onChange,
  vazio = "Nenhuma marcada",
}: {
  opcoes: OpcaoDeChip[];
  valor: string[];
  onChange: (ids: string[]) => void;
  vazio?: string;
}) {
  const [busca, setBusca] = useState("");
  const marcadas = new Set(valor);
  const alternar = (id: string) => onChange(marcadas.has(id) ? valor.filter((v) => v !== id) : [...valor, id]);
  const termo = busca.trim().toLowerCase();
  const visiveis = opcoes.filter((o) => !termo || o.rotulo.toLowerCase().includes(termo));
  const grupos = [...new Set(visiveis.map((o) => o.grupo ?? ""))];
  const longa = opcoes.length > 12;

  return (
    <div className="space-y-2">
      <div className="flex min-h-7 flex-wrap gap-1">
        {valor.length === 0 && <span className="text-xs text-admin-700/60">{vazio}</span>}
        {opcoes
          .filter((o) => marcadas.has(o.id))
          .map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => alternar(o.id)}
              title="Remover"
              className="rounded-full bg-admin-900 px-2.5 py-1 text-[11.5px] font-medium text-white hover:bg-admin-700"
            >
              {o.rotulo} ×
            </button>
          ))}
      </div>
      <div className="rounded-lg border border-admin-200">
        {longa && (
          <div className="relative border-b border-admin-200">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-admin-700/50" />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar..."
              className="w-full bg-transparent py-1.5 pl-8 pr-2 text-sm outline-none"
            />
          </div>
        )}
        <div className={cn("p-1.5", longa && "max-h-56 overflow-y-auto")}>
          {grupos.map((g) => (
            <div key={g || "_"}>
              {g && <p className="px-1.5 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-admin-700/60">{g}</p>}
              <div className="flex flex-wrap gap-1">
                {visiveis
                  .filter((o) => (o.grupo ?? "") === g)
                  .map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => alternar(o.id)}
                      aria-pressed={marcadas.has(o.id)}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[12px] transition-colors",
                        marcadas.has(o.id)
                          ? "border-admin-900 bg-admin-50 font-medium text-admin-900"
                          : "border-admin-200 bg-white text-admin-700 hover:bg-admin-50",
                        o.apagada && "opacity-60",
                      )}
                    >
                      {marcadas.has(o.id) && <Check className="h-3 w-3" />}
                      {o.rotulo}
                    </button>
                  ))}
              </div>
            </div>
          ))}
          {visiveis.length === 0 && <p className="px-1.5 py-1 text-xs text-admin-700/60">Nada encontrado.</p>}
        </div>
      </div>
    </div>
  );
}
